import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Button, Space, Tag, Tooltip, Typography, message, Modal } from 'antd'
import { EyeOutlined, UserAddOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined } from '@ant-design/icons'
import { ColumnsType, TableProps } from 'antd/es/table'
import { PlanogramPlan, SortConfig } from '../../../types/plan'
import StatusTag from '../../../components/common/StatusTag'
import dayjs from 'dayjs';

const { Text } = Typography

interface PlanTableProps {
  plans: PlanogramPlan[]
  loading: boolean
  pagination: {
    currentPage: number
    pageSize: number
    totalCount: number
  }
  sortConfig?: SortConfig
  onPaginationChange: (page: number, pageSize?: number) => void
  onPageSizeChange: (current: number, size: number) => void
  onSortChange?: (sortConfig: SortConfig) => void
  onViewDetail?: (Id: string) => void
  onAssignPersonnel?: (Id: string) => void
  onActivatePlan?: (Id: string) => void
  onPausePlan?: (Id: string) => void
  onCancelPlan?: (Id: string) => void
}

const PlanTable: React.FC<PlanTableProps> = ({
  plans,
  loading,
  pagination,
  sortConfig,
  onPaginationChange,
  onPageSizeChange,
  onSortChange,
  onViewDetail,
  onAssignPersonnel,
  onActivatePlan,
  onPausePlan,
  onCancelPlan
}) => {
  const { t, ready, i18n } = useTranslation()
  

  
  // 等待 i18n 初始化完成
  if (!ready) {
    return <div>Loading...</div>
  }
  
  const handleViewDetail = useCallback((id: string) => {
    onViewDetail?.(id)
  }, [onViewDetail])

  const handleAssignPersonnel = useCallback((id: string) => {
    onAssignPersonnel?.(id)
  }, [onAssignPersonnel])

  const handleActivatePlan = useCallback((id: string) => {
    onActivatePlan?.(id)
  }, [onActivatePlan])

  const handlePausePlan = useCallback((id: string) => {
    onPausePlan?.(id)
  }, [onPausePlan])

  const handleCancelPlan = useCallback((id: string) => {
    Modal.confirm({
      title: t('planManagement.list.actions.cancelPlanConfirmTitle'),
      content: t('planManagement.list.actions.cancelPlanConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: () => {
        onCancelPlan?.(id)
      }
    })
  }, [onCancelPlan, t])

  const handleTableChange: TableProps<PlanogramPlan>['onChange'] = useCallback((
    _pagination: any,
    _filters: any,
    sorter: any
  ) => {
    if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
      const newSortConfig: SortConfig = {
        field: sorter.field as string,
        direction: sorter.order === 'ascend' ? 'asc' : 'desc'
      }
      onSortChange?.(newSortConfig)
    }
  }, [onSortChange])

  const getPlanTypeColor = (planType: string) => {
    const colorMap: Record<string, string> = {
      'Major': 'red',
      'Regular': 'blue',
      'Minor': 'green',
      'Urgent': 'orange',
      'Special': 'purple'
    }
    return colorMap[planType] || 'default'
  }

  const getPlanTypeText = (planType: string) => {
    const textMap: Record<string, string> = {
      'Major': t('planManagement.list.planTypes.Major'),
      'Regular': t('planManagement.list.planTypes.Regular'),
      'Minor': t('planManagement.list.planTypes.Minor'),
      'Urgent': t('planManagement.list.planTypes.Urgent'),
      'Special': t('planManagement.list.planTypes.Special')
    }
    return textMap[planType] || planType
  }

  const getActionButtons = (record: PlanogramPlan) => {
    const buttons = []

    // View Detail button - always available
    buttons.push(
      <Tooltip title={t('planManagement.list.actions.viewDetail')} key="detail">
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
        />
      </Tooltip>
    )

    // Assign button - available for Pending, InProgress, and Paused status
    if (['Pending', 'InProgress', 'Paused'].includes(record.status)) {
      const isInProgress = record.status === 'InProgress'
      const buttonTitle = isInProgress ? t('planManagement.list.actions.modifyPersonnel') : t('planManagement.list.actions.assignPersonnel')
      
      buttons.push(
        <Tooltip title={buttonTitle} key="assign">
          <Button
            type="link"
            size="small"
            icon={<UserAddOutlined />}
            onClick={() => handleAssignPersonnel(record.id)}
          />
        </Tooltip>
      )
    }

    // Activate button - only for Pending status
    if (record.status === 'Pending') {
      buttons.push(
        <Tooltip title={t('planManagement.list.actions.activatePlan')} key="activate">
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleActivatePlan(record.id)}
          />
        </Tooltip>
      )
    }

    // Pause button - only for InProgress status
    if (record.status === 'InProgress') {
      buttons.push(
        <Tooltip title={t('planManagement.list.actions.pausePlan')} key="pause">
          <Button
            type="link"
            size="small"
            icon={<PauseCircleOutlined />}
            onClick={() => handlePausePlan(record.id)}
            style={{ color: '#faad14' }}
          />
        </Tooltip>
      )
    }

    // Reactivate button - only for Paused status
    if (record.status === 'Paused') {
      buttons.push(
        <Tooltip title={t('planManagement.list.actions.reactivatePlan')} key="reactivate">
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleActivatePlan(record.id)}
            style={{ color: '#52c41a' }}
          />
        </Tooltip>
      )
    }

    // Cancel button - available for Paused status
    if (record.status !== 'Cancelled' && record.status !== 'Completed') {
      buttons.push(
        <Tooltip title={t('planManagement.list.actions.cancelPlan')} key="cancel">
          <Button
            type="link"
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleCancelPlan(record.id)}
            style={{ color: '#ff4d4f' }}
          />
        </Tooltip>
      )
    }

    return buttons
  }

  const formatDateTime = (timestamp: number): string => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
  };

  const columns: ColumnsType<PlanogramPlan> = useMemo(() => [
    {
      title: t('planManagement.list.columns.planCode'),
      dataIndex: 'planCode',
      key: 'planCode',
      width: 180,
      fixed: 'left',
      // sorter: true,
      // sortOrder: sortConfig?.field === 'planCode' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {text}
        </Text>
      )
    },
    {
      title: t('planManagement.list.columns.planName'),
      dataIndex: 'planName',
      key: 'planName',
      width: 250,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'planName' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text ellipsis style={{ maxWidth: 230 }}>
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: t('planManagement.list.columns.planType'),
      dataIndex: 'planType',
      key: 'planType',
      width: 100,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'planType' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (planType: string) => (
        <Tag color={getPlanTypeColor(planType)}>
          {getPlanTypeText(planType)}
        </Tag>
      )
    },
    {
      title: t('planManagement.list.columns.templateReference'),
      dataIndex: 'templateReference',
      key: 'templateReference',
      width: 120,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'templateReference' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {text}
        </Text>
      )
    },
    {
      title: t('planManagement.list.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'status' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (status: string) => <StatusTag status={status} type="plan" />
    },
    {
      title: t('planManagement.list.columns.templateOwner'),
      dataIndex: 'templateOwner',
      key: 'templateOwner',
      width: 120,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'templateOwner' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (text: string) => text || '-'
    },
    {
      title: t('planManagement.list.columns.currentMilestone'),
      dataIndex: 'currentMilestone',
      key: 'currentMilestone',
      width: 150,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text ellipsis style={{ maxWidth: 130 }}>
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: t('planManagement.list.columns.storeCount'),
      dataIndex: 'storeCount',
      key: 'storeCount',
      width: 100,
      align: 'right',
      // sorter: true,
      // sortOrder: sortConfig?.field === 'storeCount' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (count: number) => (
        <Text strong>{count.toLocaleString()}</Text>
      )
    },
    {
      title: t('planManagement.list.columns.generateTime'),
      dataIndex: 'planGenerateTime',
      key: 'planGenerateTime',
      width: 120,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'planGenerateTime' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (timestamp: number) => (formatDateTime(timestamp))
    },
    {
      title: t('planManagement.list.columns.plannedTime'),
      dataIndex: 'plannedStartTime',
      key: 'plannedStartTime',
      width: 180,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'plannedStartTime' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (startTime: number, record: PlanogramPlan) => (
        <div style={{ fontSize: '12px' }}>
          <div>{formatDateTime(startTime)}</div>
          <div style={{ color: '#666' }}>
            {t('common.to') || '至'} {formatDateTime(record.plannedEndTime)}
          </div>
        </div>
      )
    },
    {
      title: t('planManagement.list.columns.lastMilestonePlanTime'),
      dataIndex: 'lastMilestonePlanStartTime',
      key: 'lastMilestonePlanStartTime',
      width: 180,
      // sorter: true,
      // sortOrder: sortConfig?.field === 'plannedStartTime' ? 
      //   (sortConfig.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (lastMilestonePlanStartTime: number, record: PlanogramPlan) => (
        <div style={{ fontSize: '12px' }}>
          <div>{formatDateTime(lastMilestonePlanStartTime)}</div>
          <div style={{ color: '#666' }}>
            {t('common.to') || '至'} {formatDateTime(record.lastMilestonePlanEndTime)}
          </div>
        </div>
      )
    },
    {
      title: t('planManagement.list.columns.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record: PlanogramPlan) => (
        <Space size="small">
          {getActionButtons(record)}
        </Space>
      )
    }
  ], [handleViewDetail, handleAssignPersonnel, handleActivatePlan, handlePausePlan, handleCancelPlan, sortConfig, t])

  return (
    <Table<PlanogramPlan>
      columns={columns}
      dataSource={plans}
      rowKey="id"
      loading={loading}
      onChange={handleTableChange}
      scroll={{ x: 1400, y: 500 }}
      pagination={{
        current: pagination.currentPage,
        pageSize: pagination.pageSize,
        total: pagination.totalCount,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          t('planManagement.list.pagination.showTotal', { start: range[0], end: range[1], total }),
        pageSizeOptions: ['20', '50', '100'],
        onChange: onPaginationChange,
        onShowSizeChange: onPageSizeChange,
      }}
      size="small"
      bordered
    />
  )
}

export default PlanTable